import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PresenterScriptPanel } from '@/components/presenter/ScriptPanel';
import { PresenterAvatarPanel } from '@/components/presenter/AvatarPanel';
import { PresenterVoicePanel } from '@/components/presenter/VoicePanel';
import { PresenterRenderPanel } from '@/components/presenter/RenderPanel';
import { PresenterCutawayPanel } from '@/components/presenter/CutawayPanel';

const isBusy = (v) => v && (v.status === 'rendering' || v.image_status === 'generating' || v.audio_status === 'generating' || v.mix_status === 'mixing');

export default function PresenterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [saving, setSaving] = useState(false);
  const titleTimer = useRef(null);

  const refresh = useCallback(async () => {
    try { const r = await api.get(`/presenter/${id}`); setVideo(r.data); return r.data; }
    catch { toast.error('Presenter video not found'); navigate('/presenter'); }
  }, [id, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!isBusy(video)) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [video, refresh]);

  const update = async (patch, { silent = false } = {}) => {
    setSaving(true);
    try { const r = await api.put(`/presenter/${id}`, patch); setVideo(r.data); if (!silent) toast.success('Saved'); return r.data; }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const onTitle = (title) => {
    setVideo(v => ({ ...v, title }));
    clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(() => update({ title }, { silent: true }), 600);
  };

  if (!video) return <div className="p-10 text-[#8A8A93] text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6 max-w-7xl mx-auto" data-testid="presenter-editor">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="presenter-back" onClick={() => navigate('/presenter')} className="w-9 h-9 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-[#C9C9CF] flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
        <input data-testid="presenter-title-input" value={video.title} onChange={e => onTitle(e.target.value)}
          className="bg-transparent text-xl sm:text-2xl font-bold text-white outline-none flex-1 min-w-0 border-b border-transparent focus:border-[#E879F9]/40 transition-colors" />
        {saving && <Loader2 className="w-4 h-4 text-[#8A8A93] animate-spin" />}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-5">
        <div className="space-y-5">
          <PresenterScriptPanel video={video} onSave={(script) => update({ script }, { silent: true })} />
          <PresenterVoicePanel video={video} onUpdate={update} onRefresh={refresh} />
        </div>
        <div className="space-y-5">
          <PresenterAvatarPanel video={video} onUpdate={update} onRefresh={refresh} />
          <PresenterRenderPanel video={video} onUpdate={update} onRefresh={refresh} />
          <PresenterCutawayPanel video={video} onRefresh={refresh} />
        </div>
      </div>
    </div>
  );
}
